export interface EnvConfig {
  /** Base URL for API requests */
  apiBaseUrl: string;
  /** Current environment name (local, ppe, prod) */
  envName: 'local' | 'ppe' | 'prod';
  /** Application Insights instrumentation key (optional) */
  appInsightsKey: string | undefined;
  /** Debug mode flag */
  debugMode: boolean;
}

const REQUIRED_ENV_VARS = ['VITE_ENV_NAME'] as const;

// VITE_API_BASE_URL is validated separately: empty is allowed in dev (uses Vite proxy)
const OPTIONAL_IN_DEV = ['VITE_API_BASE_URL'] as const;

export function validateEnv(): void {
  const missingVars: string[] = [];

  for (const envVar of REQUIRED_ENV_VARS) {
    if (!import.meta.env[envVar]) {
      missingVars.push(envVar);
    }
  }

  // In production, VITE_API_BASE_URL is required
  if (!import.meta.env.DEV) {
    for (const envVar of OPTIONAL_IN_DEV) {
      if (!import.meta.env[envVar]) {
        missingVars.push(envVar);
      }
    }
  }

  if (missingVars.length > 0) {
    const errorMessage = `Missing required environment variables: ${missingVars.join(', ')}
    
Please ensure your environment file (env/.env.local, env/.env.ppe, or env/.env.prod) contains:
${missingVars.map((v) => `  ${v}=<value>`).join('\n')}

See env/.env.template for documentation of all environment variables.`;

    if (import.meta.env.DEV) {
      console.error('🚨 Environment Configuration Error:\n', errorMessage);
    }

    throw new Error(errorMessage);
  }
}

export function getEnvConfig(): EnvConfig {
  const envName = import.meta.env.VITE_ENV_NAME;

  if (!['local', 'ppe', 'prod'].includes(envName)) {
    console.warn(
      `Invalid VITE_ENV_NAME: "${envName}". Expected "local", "ppe", or "prod". Defaulting to "local".`
    );
  }

  const appInsightsKey = import.meta.env.VITE_APP_INSIGHTS_KEY;

  return {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
    envName: (['local', 'ppe', 'prod'].includes(envName)
      ? envName
      : 'local') as EnvConfig['envName'],
    appInsightsKey,
    debugMode: import.meta.env.VITE_DEBUG_MODE === 'true',
  };
}

let envConfigInstance: EnvConfig | null = null;

export function env(): EnvConfig {
  if (!envConfigInstance) {
    validateEnv();
    envConfigInstance = getEnvConfig();
  }
  return envConfigInstance;
}

export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

export function isProduction(): boolean {
  return import.meta.env.PROD;
}

export function isDebugMode(): boolean {
  return env().debugMode;
}
